import { PageHeader, Loading } from '../../components/office';
import { useState, useEffect } from 'react';
import { userSettingsApi, authApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { Switch } from '@verlo/shared/components/ui/switch';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import { Avatar } from '../../components/people/Person';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './settings/Section';
import MyDevicesCard from './MyDevicesCard';
import TwoFactorCard from './TwoFactorCard';

// My settings (Back office sweep, Oct 2026). Your own: who you are, where you land when you
// sign in, your alerts, two-factor and where you're signed in. Each saves as you change it.

const UserSettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ landing_page: 'tables', notifications: true });
  // RBAC v2 — permission-aware landing options served from the backend
  const [landingOptions, setLandingOptions] = useState([]);
  const [landing, setLanding] = useState('/tables');

  useEffect(() => {
    Promise.all([
      userSettingsApi.get().catch(() => ({ data: {} })),
      authApi.getLandingOptions().catch(() => ({ data: { options: [], current: '/tables' } })),
    ]).then(([s, lo]) => {
      setSettings((prev) => ({ ...prev, ...s.data }));
      setLandingOptions(lo.data.options || []);
      setLanding(lo.data.current || '/tables');
    }).finally(() => setLoading(false));
  }, []);

  const pickLanding = async (path) => {
    const was = landing;
    setLanding(path);
    try {
      await authApi.setLandingPath(path);
      // The stored user carries it, so the next sign-in lands there.
      try { const me = await authApi.getMe(); localStorage.setItem('verlo_user', JSON.stringify(me.data)); } catch { /* no-op */ }
      toast.success(`You'll land on ${landingOptions.find((o) => o.path === path)?.label || path}`);
    } catch (err) {
      setLanding(was);
      toast.error(err.response?.data?.detail || "Couldn't save that");
    }
  };

  const setAlerts = async (on) => {
    const next = { ...settings, notifications: on };
    setSettings(next);
    try { await userSettingsApi.update(next); } catch (err) {
      setSettings(settings);
      toast.error(err.response?.data?.detail || "Couldn't save that");
    }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="user-settings-page">
      <PageHeader title="My settings" purpose="Yours alone: where you land, your alerts, and how you sign in. Each saves as you change it." />

      {loading ? <Loading /> : (
        <>
          <Card>
            <CardContent className="flex items-center gap-4">
              <Avatar name={user?.name || user?.email || 'You'} className="h-12 w-12 text-[15px]" />
              <div className="min-w-0">
                <p className="font-heading text-[16px] font-extrabold text-ink">{user?.name || 'You'}</p>
                <p className="text-[13px] text-ink-soft">{user?.email}</p>
                {/* Verlo's own staff have a platform role; their legacy `role` says nothing (#180). */}
                <p className="text-[12px] capitalize text-ink-faint">{user?.platform_role ? `Verlo ${user.platform_role}` : user?.role}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Where you land</CardTitle>
              <CardDescription>The page you see when you sign in. Only pages you can open are here.</CardDescription>
            </CardHeader>
            <CardContent className="py-2" data-testid="landing-options">
              {landingOptions.length === 0 && <p className="py-2 text-[13px] text-ink-faint">No landing pages available for your role.</p>}
              <div className="divide-y divide-hairline">
                {landingOptions.map((o) => {
                  const on = landing === o.path;
                  return (
                    <button key={o.path} type="button" role="radio" aria-checked={on} onClick={() => !on && pickLanding(o.path)}
                      className="flex w-full items-center justify-between gap-3 py-2.5 text-left hover:bg-hover-tint"
                      data-testid={`landing-option-${o.path.replace(/\//g, '-')}`}>
                      <span className={`text-[14px] ${on ? 'font-semibold text-verlo' : 'text-ink'}`}>{o.label}</span>
                      {on ? <Check size={16} className="text-verlo" /> : <span className="h-4 w-4 rounded-full border border-hairline-strong" />}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <label className="flex items-center justify-between gap-4">
                <span className="text-[14px] text-ink">Alerts for me<span className="block text-[12.5px] text-ink-soft">Orders coming in, stock running low and the like, while you're in the back office.</span></span>
                <Switch checked={!!settings.notifications} onCheckedChange={setAlerts} data-testid="user-alerts" />
              </label>
            </CardContent>
          </Card>

          <TwoFactorCard />
          <MyDevicesCard />
        </>
      )}
    </div>
  );
};

export default UserSettingsPage;
